<template>
    <ToggleSimple ref="toggleComponent" @update:open="toggleOpen = $event" :default="defaultToggle">
        <template #toggle>
            <div class="left">
                <div v-if="$te(`fields.${name}.label`)" class="toggle-text">
                    {{ $t(`fields.${name}.label`) }}
                    <span v-if="count.selected && count.selected > 0">{{ count.selected }}</span>
                </div>
                <div v-if="$te(`fields.${name}.description`)" class="toggle-description">
                    {{ $t(`fields.${name}.description`) }}
                </div>
            </div>
            <div class="right">
                <div v-if="toggleOpen" @click.stop="toggleExpertMode" class="btn-link">
                    {{ $t(expertMode ? 'common.basic_mode_1' : 'common.expert_mode_1') }}
                </div>
                <div v-if="isActive" class="reset" @click="resetSection">
                    <div class="icon-reset-pos"></div>
                </div>
                <div class="toggle-icon"></div>
            </div>
        </template>

        <template #content>
            <div :class="cssClassOptions">
                <template v-if="!expertMode">
                    <CustomIncludesExcludes
                        v-model="options"
                        v-if="count.selected > 0"
                        :inputKey="name"
                        :showRequired="true"
                        :smHorizontal="!narrow"
                        @update:modelValue="onListsChanged"
                    />

                    <CustomSuggest 
                        :inputKey="name"
                        :fetchSuggestionsFn="fetchSuggestions"
                        :excludedValues="excludedValues"
                        @select-suggestion="addSuggestion"
                        :showLabel="false"
                        :showDescription="false"
                        :allowCommaSeparated="true"
                        :selectMultiple="true"
                        ref="suggest"
                    />
                </template>

                <template v-else>
                    <CustomTextQuery
                        v-model="options.custom"
                        @update:modelValue="onCustomQueryChanged"
                        :showLabel="false"
                    />
                </template>
            </div>
        </template>
    </ToggleSimple>
</template>

<script>
    import { fetchSuggestions } from '@/services/freeApi';
    import { nextTick } from 'vue';
    import { usePopupStore } from '@/stores/utils/popup';
    import ToggleSimple from '@/components/elements/misc/ToggleSimple';
    import CustomIncludesExcludes from '@/components/elements/inputs/CustomIncludesExcludes';
    import CustomSuggest from '@/components/elements/inputs/CustomSuggest';
    import CustomTextQuery from '@/components/elements/inputs/CustomTextQuery';

    export default {
        name: "OptionsText",
        components: {
            ToggleSimple,
            CustomIncludesExcludes,
            CustomSuggest,
            CustomTextQuery
        },
        props: {
            modelValue: {
                type: Object,
                default: () => ({ includes: [], excludes: [], required: [], custom: '' })
            },
            defaultVal: {
                type: Object,
                default: () => ({ includes: [], excludes: [], required: [], custom: '' })
            },
            defaultToggle: {
                type: Boolean,
                default: false
            },
            showDescriptions: {
                type: Boolean,
                default: true
            },
            cssClassOptions: {
                type: String,
                default: 'options-type-4'
            },
            narrow: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                name: 'text_search',
                expertMode: false,
                toggleOpen: this.defaultToggle,
                options: {
                    includes: this.modelValue?.includes || [],
                    excludes: this.modelValue?.excludes || [],
                    required: this.modelValue?.required || [],
                    custom: this.modelValue?.custom || ''
                }
            };
        },
        computed: {
            isActive() {
                return JSON.stringify(this.modelValue) !== JSON.stringify(this.defaultVal);
            },
            excludedValues() {
                return [
                    ...this.options.includes.map(app => app.value),
                    ...this.options.excludes.map(app => app.value),
                    ...this.options.required.map(app => app.value)
                ];
            },
            count() {
                return {
                    total: this.options.includes.length + this.options.excludes.length + this.options.required.length,
                    selected: this.options.includes.length + this.options.excludes.length + this.options.required.length + (this.options.custom ? 1 : 0),
                    includes: this.options.includes.length + this.options.required.length,
                    excludes: this.options.excludes.length,
                };
            }
        },
        methods: {
            resetSection(event = null) {
                if (event) {
                    event.stopPropagation();
                }
                this.options = {
                    includes: [],
                    excludes: [],
                    required: [],
                    custom: ''
                };

                if(this.$refs.suggest && typeof this.$refs.suggest.clearSearch === 'function') {
                    this.$refs.suggest.clearSearch();
                }

                this.emitUpdate();
            },
            async fetchSuggestions(query) {
                try {
                    const response = await fetchSuggestions({
                        type: 'text',
                        q: query
                    });
                    return response.data.suggest.map(suggestion => ({
                        value: suggestion.term,
                        text: suggestion.term,
                    }));
                } catch {
                    return [];
                }
            },
            addSuggestion(suggestion) {
                this.options.includes.push(suggestion);
                this.emitUpdate();
            },
            onListsChanged(newValue) {
                this.options = {
                    includes: newValue.includes,
                    excludes: newValue.excludes,
                    required: newValue.required,
                    custom: ''
                };
                this.emitUpdate();
            },
            onCustomQueryChanged(newValue) {
                this.options = {
                    includes: [],
                    excludes: [],
                    required: [],
                    custom: newValue
                };
                this.emitUpdate();
            },
            toggleExpertMode() {
                if(this.expertMode) {
                    this.warningBasicMode();
                } else {
                    this.warningExpertMode();
                }
            },
            emitUpdate() {
                nextTick(() => {
                    this.$emit('update:modelValue', this.options);
                });
            },
            warningExpertMode() {
                if(this.options.includes.length > 0 || this.options.excludes.length > 0 || this.options.required.length > 0) {
                    usePopupStore().showPopup('confirm', {
                        title: 'popups.confirm_expert_mode.title',
                        description: 'popups.confirm_expert_mode.description',
                        btnConfirm: "popups.confirm_expert_mode.button_confirm",
                        confirmFunction: () => this.setExpertMode(),
                        danger: true,
                    });
                } else {
                    this.setExpertMode()
                }
            },
            setExpertMode() {
                if(this.options.includes.length > 0 || this.options.excludes.length > 0 || this.options.required.length > 0) {
                    const includes = this.options.includes.map(app => app.value).map(value => value.includes(' ') ? `"${value}"` : value).join(' OR ');
                    const excludes = this.options.excludes.map(app => app.value).map(value => value.includes(' ') ? `-"${value}"` : `-${value}`).join(' AND ');
                    const required = this.options.required.map(app => app.value).map(value => value.includes(' ') ? `"${value}"` : value).join(' AND ');
                    this.options.custom = [includes, excludes, required].filter(item => item).join(' AND ');
                    this.options.includes = [];
                    this.options.excludes = [];
                }
                this.emitUpdate();
                this.expertMode = true;
            },
            warningBasicMode() {
                if(this.options.custom) {
                    usePopupStore().showPopup('confirm', {
                        title: 'popups.confirm_basic_mode.title',
                        description: 'popups.confirm_basic_mode.description',
                        btnConfirm: "popups.confirm_basic_mode.button_confirm",
                        confirmFunction: () => this.setBasicMode(),
                        danger: true,
                    });
                } else {
                    this.setBasicMode()
                }
            },
            setBasicMode() {
                this.options.custom = '';
                this.emitUpdate();
                this.expertMode = false;
            }
        },
        watch: {
            modelValue: {
                handler(newVal) {
                    this.options = {
                        includes: newVal?.includes || [],
                        excludes: newVal?.excludes || [],
                        required: newVal?.required || [],
                        custom: newVal?.custom || ''
                    };
                },
                immediate: true
            }
        },
        mounted() {
            if(this.options.custom) {
                this.expertMode = true;
            }
        }
    }
</script>